import {
  Avatar,
  Box,
  Button,
  HStack,
  Heading,
  Spinner,
  Stack,
  Text,
  VStack,
  useToast,
  Icon,
  IconButton,
  Progress,
  Flex,
} from '@chakra-ui/react';
import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getOrderDetails } from '../../../api/order';
import axios from 'axios';
import { FiChevronLeft, FiClock, FiPhone, FiInfo, FiMapPin, FiTruck } from 'react-icons/fi';
import { IoMdFlask } from 'react-icons/io';
import { MdSecurity } from 'react-icons/md';

export const PackageDetails = () => {
  const [packages, setPackages] = useState({});
  const [packageOrder, setPackageOrder] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const searchObject = new URLSearchParams(location?.search);
  const orderId = searchObject.get('orderId');
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleCancelled = async () => {
    try {
      setLoading(true);
      const res = await axios.put(
        `${process.env.REACT_APP_SERVER}/labs/orders/cancel/${orderId}`
      );

      setPackageOrder(res?.data?.order);
      toast({
        title: res?.data?.message,
        duration: 9000,
        status: 'success',
        isClosable: true,
      });
    } catch (error) {
      console.error('Error cancelling order:', error);
      toast({
        title: 'error in cancelling the order',
        duration: 9000,
        status: 'error',
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchOrder = useCallback(async () => {
    if (orderId) {
      try {
        setLoading(true);
        const res = await getOrderDetails(orderId);
        setPackages(res.packages || {});
        setPackageOrder(res);
      } catch (error) {
        console.error('error while fetching orderDetails', error);
      } finally {
        setLoading(false);
      }
    }
  }, [orderId]);

  useEffect(() => {
    fetchOrder();
  }, [fetchOrder]);

  const getStepProgress = (status) => {
    switch (status?.toLowerCase()) {
      case 'pending': return 25;
      case 'assigned': return 50;
      case 'collection': return 75;
      case 'completed': return 100;
      default: return 25;
    }
  };

  if (loading && !packageOrder) {
    return (
      <Box h="80vh" display="flex" alignItems="center" justifyContent="center">
        <Spinner size="xl" color="teal.500" thickness="4px" />
      </Box>
    );
  }

  return (
    <Box maxW="800px" mx="auto" p={4} pb={20}>
      <HStack spacing={4} mb={6} justify="center" position="relative">
        <IconButton 
          icon={<FiChevronLeft />} 
          variant="ghost" 
          borderRadius="full" 
          fontSize="24px"
          position="absolute"
          left={0}
          onClick={() => navigate(-1)}
        />
        <Heading color={'#0C727E'} size="md">Order Tracking</Heading>
      </HStack>

      <Stack spacing={6}>
        {/* Status Banner */}
        <Box 
          bg="white" 
          p={6} 
          borderRadius="3xl" 
          boxShadow="sm" 
          borderWidth="1px" 
          borderColor="gray.100"
        >
          <HStack justify="space-between" align="center" mb={6}>
            <VStack align="flex-start" spacing={1}>
              <Heading size="lg" fontWeight="800">Order {packageOrder?.orderStatus || 'pending'}</Heading>
              <Text color="gray.400" fontSize="sm">Estimated completion: Today, 6:00 PM</Text>
            </VStack>
            <Box bg="orange.50" p={4} borderRadius="2xl">
              <Icon as={FiClock} boxSize={8} color="orange.400" />
            </Box>
          </HStack>

          <Box px={4}>
            <Progress 
              value={getStepProgress(packageOrder?.orderStatus)} 
              colorScheme="teal" 
              size="sm" 
              borderRadius="full" 
              mb={4}
            />
            <HStack justify="space-between">
              {['Placed', 'Assigned', 'Collection', 'Completed'].map((step, idx) => {
                const isActive = getStepProgress(packageOrder?.orderStatus) >= (idx + 1) * 25;
                return (
                  <VStack key={step} spacing={2}>
                    <Box 
                      w={4} h={4} 
                      borderRadius="full" 
                      bg={isActive ? 'teal.500' : 'gray.200'}
                      border="4px solid"
                      borderColor={isActive ? 'teal.50' : 'transparent'}
                    />
                    <Text fontSize="xs" fontWeight="bold" color={isActive ? 'teal.600' : 'gray.400'}>
                      {step}
                    </Text>
                  </VStack>
                );
              })}
            </HStack>
          </Box>
        </Box>

        {/* Package Card */}
        <Box 
          bg="white" 
          borderRadius="3xl" 
          overflow="hidden" 
          borderWidth="1px" 
          borderColor="gray.100"
          boxShadow="sm"
        >
          <Box bg="teal.600" h={2} />
          <Box p={6}>
            <HStack justify="space-between" align="flex-start" mb={4}>
              <VStack align="flex-start" spacing={1}>
                <Text fontSize="xs" fontWeight="800" color="teal.600" letterSpacing="wider">HEALTH PACKAGE</Text>
                <Heading size="md" fontWeight="800">{packages?.name}</Heading>
              </VStack>
              <VStack align="flex-end" spacing={0}>
                <Text fontSize="xs" color="gray.400">Total</Text>
                <Heading size="md" fontWeight="800">₹{packageOrder?.price || packages?.price}</Heading>
              </VStack>
            </HStack>
            
            <HStack spacing={8} mt={6}>
              <HStack spacing={3}>
                <Box bg="teal.50" p={2} borderRadius="lg">
                  <Icon as={IoMdFlask} color="teal.600" />
                </Box>
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="10px" color="gray.400" fontWeight="bold">Tests</Text>
                  <Text fontSize="xs" fontWeight="800">{packages?.tests?.length || 0}+ Bio-markers</Text>
                </VStack>
              </HStack>
              <HStack spacing={3}>
                <Box bg="teal.50" p={2} borderRadius="lg">
                  <Icon as={MdSecurity} color="teal.600" />
                </Box>
                <VStack align="flex-start" spacing={0}>
                  <Text fontSize="10px" color="gray.400" fontWeight="bold">Audit</Text>
                  <Text fontSize="xs" fontWeight="800">ISO Verified</Text>
                </VStack>
              </HStack>
            </HStack>
          </Box>
        </Box>

        {/* Appointment Info */}
        <VStack align="flex-start" spacing={4}>
          <Heading size="sm" color="gray.800">Appointment Info</Heading>
          <Box 
            bg="white" 
            p={6} 
            borderRadius="3xl" 
            w="100%" 
            borderWidth="1px" 
            borderColor="gray.100"
            boxShadow="sm"
          >
            <Flex wrap="wrap">
              <VStack align="flex-start" w="50%" mb={4}>
                <Text fontSize="xs" color="gray.400">Date:</Text>
                <Text fontSize="sm" fontWeight="800">{packageOrder?.appointmentDate || packageOrder?.paymentDetails?.appointmentDate || 'N/A'}</Text>
              </VStack>
              <VStack align="flex-start" w="50%" mb={4}>
                <Text fontSize="xs" color="gray.400">Time Slot:</Text>
                <Text fontSize="sm" fontWeight="800">{packageOrder?.appointmentTime || packageOrder?.paymentDetails?.appointmentTime || 'N/A'}</Text>
              </VStack>
              <VStack align="flex-start" w="50%">
                <Text fontSize="xs" color="gray.400">Patient:</Text>
                <Text fontSize="sm" fontWeight="800">{packageOrder?.patientDetails?.name || 'Self'}</Text>
              </VStack>
              <VStack align="flex-start" w="50%">
                <Text fontSize="xs" color="gray.400">Sample Mode:</Text>
                <Text fontSize="sm" fontWeight="800">{packageOrder?.consumerDetails?.address ? 'Home Collection' : 'Lab Visit'}</Text>
              </VStack>
            </Flex>
          </Box>
        </VStack>

        {/* Patient Details */}
        <VStack align="flex-start" spacing={4}>
          <Heading size="sm" color="gray.800">Patient Details</Heading>
          <Box 
            bg="white" 
            p={6} 
            borderRadius="3xl" 
            w="100%" 
            borderWidth="1px" 
            borderColor="gray.100"
            boxShadow="sm"
          >
            <HStack spacing={4} mb={6}>
              <Avatar size="md" name={packageOrder?.consumerDetails?.name} bg="teal.50" color="teal.600" />
              <VStack align="flex-start" spacing={0} flex={1}>
                <Text fontWeight="800" fontSize="md">{packageOrder?.consumerDetails?.name || 'User'}</Text>
                <Text fontSize="xs" color="gray.400">Booking ID: {packageOrder?.orderId || ('#' + packageOrder?._id?.slice(-6).toUpperCase())}</Text>
              </VStack>
              <IconButton icon={<FiPhone />} isRound colorScheme="teal" variant="ghost" bg="teal.50" />
            </HStack>

            <HStack spacing={4} align="flex-start">
              <Box bg="red.50" p={2} borderRadius="lg">
                <Icon as={FiMapPin} color="red.400" />
              </Box>
              <VStack align="flex-start" spacing={0}>
                <Text fontSize="10px" color="gray.400" fontWeight="bold">Sample Collection At</Text>
                <Text fontSize="xs" fontWeight="800" color="gray.600">
                  {packageOrder?.consumerDetails?.address?.secondaryAddress || 'Lab Center'}
                </Text>
              </VStack>
            </HStack>
          </Box>
        </VStack>

        <Button 
          leftIcon={<FiTruck />} 
          colorScheme="teal" 
          size="lg" 
          borderRadius="2xl" 
          py={8}
          fontSize="md"
          fontWeight="800"
          rightIcon={<FiChevronLeft style={{ transform: 'rotate(180deg)' }} />}
        >
          Track Phlebotomist Live
        </Button>

        <HStack bg="gray.50" p={4} borderRadius="2xl" spacing={3}>
          <Icon as={FiInfo} color="gray.400" />
          <Text fontSize="xs" color="gray.500" lineHeight="tall">
            Need help with your booking? Contact our support at 1800-DLABS.
          </Text>
        </HStack>

        {packageOrder?.orderStatus?.toLowerCase() === 'pending' && (
          <Button 
            variant="link" 
            colorScheme="red" 
            size="sm" 
            onClick={handleCancelled}
          >
            Cancel Booking
          </Button>
        )}
      </Stack>
    </Box>
  );
};
